import { apiFetch } from './client';

/**
 * A `client` is an external person at a partner utility (e.g. an ops analyst who
 * posts/collects files). Clients authenticate over SFTP by SSH key and normally
 * have no portal (Keycloak) login — they are the external counterpart to an
 * internal Keycloak user. See PartnerDetailPage → Clients tab.
 */
export interface Client {
  id: string;
  name: string;
  email: string;
  company: string | null;
  partnerId: string | null;
  notes: string | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
}

export interface ClientFilters {
  search?: string;
  partnerId?: string;
  page?: number;
  limit?: number;
}

export interface CreateClientPayload {
  name: string;
  email: string;
  company?: string;
  partnerId?: string;
  notes?: string;
}

export type UpdateClientPayload = Partial<CreateClientPayload>;

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const clientsApi = {
  list: (filters: ClientFilters = {}): Promise<ClientListResponse> => {
    const qs = buildQuery(filters as Record<string, string | number | boolean | undefined>);
    return apiFetch<ClientListResponse>(`/clients${qs}`);
  },

  get: (id: string): Promise<Client> => {
    return apiFetch<Client>(`/clients/${id}`);
  },

  create: (data: CreateClientPayload): Promise<Client> => {
    return apiFetch<Client>('/clients', { method: 'POST', data });
  },

  update: (id: string, data: UpdateClientPayload): Promise<Client> => {
    return apiFetch<Client>(`/clients/${id}`, { method: 'PUT', data });
  },

  remove: (id: string): Promise<{ message: string }> => {
    return apiFetch<{ message: string }>(`/clients/${id}`, { method: 'DELETE' });
  },
};
